import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from '@mui/material';
import {
  Savings,
  Store,
  QrCode,
  LocalOffer,
  Star,
  LocationOn,
  Celebration,
  CheckCircle,
  Discount,
  LocalActivity,
  Security,
  Support,
  Restaurant,
  TrendingUp,
  AccountBalance,
  Category,
  Verified,
  Speed,
  Smartphone,
  NewReleases,
  Loyalty,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Typewriter from 'typewriter-effect';
import BackToTop from '../components/BackToTop';

import SEO from '../components/SEO';

const CustomersPage = () => {

  const howItWorks = [
    {
      step: '1',
      icon: <Star sx={{ fontSize: 40, color: '#e3662d' }} />,
      title: 'Join 10D',
      description: 'Sign up in minutes with our simple registration process.',
    },
    {
      step: '2',
      icon: <LocationOn sx={{ fontSize: 40, color: '#5a9b9a' }} />,
      title: 'Find Businesses',
      description: 'Browse our directory of partner businesses in your area.',
    },
    {
      step: '3',
      icon: <QrCode sx={{ fontSize: 40, color: '#e6b8a2' }} />,
      title: 'Visit & Scan',
      description: 'Visit any partner business, scan your QR code.',
    },
    {
      step: '4',
      icon: <Celebration sx={{ fontSize: 40, color: '#5a9b9a' }} />,
      title: 'Enjoy Savings',
      description: 'Get exclusive discounts while supporting local businesses.',
    },
  ];

  const subscriptionPlans = [
    {
      name: 'Monthly',
      price: '₹99',
      period: '/month',
      features: [
        'Access to all 10D partner businesses',
        'Exclusive member discounts',
        'QR code verification',
        'Local business discovery',
      ],
      popular: true,
    },
    {
      name: 'Quarterly',
      price: '₹299',
      period: '/3 months',
      features: [
        'All monthly benefits',
        'Priority customer support',
        'Early access to new partners',
        'Special quarterly offers',
      ],
      popular: false,
    },
    {
      name: 'Half Yearly',
      price: '₹499',
      period: '/6 months',
      features: [
        'All quarterly benefits',
        'Premium member status',
        'Exclusive events access',
        'Partner business insights',
      ],
      popular: false,
    },
    {
      name: 'Yearly',
      price: '₹999',
      period: '/year',
      features: [
        'All half-yearly benefits',
        'VIP member status',
        'Annual bonus discounts',
        'Priority booking at events',
      ],
      popular: false,
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <SEO
        title="For Customers"
        description="Join 10D Stores as a customer to discover local businesses, get exclusive discounts, and save money."
        canonical="https://10dstores.in/customers"
      />
      {/* Hero Section with Video Background */}
      <Box
        sx={{
          position: 'relative',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          color: 'white',
        }}
      >
        {/* Video Background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={require('../assets/videos/customer_page/customer_page_jumbotron_video.mp4')} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Dark Overlay for better text readability */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 1,
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '4.5rem',
              lineHeight: 1.1,
              color: 'white',
              mb: 6,
              textShadow: '0 2px 4px rgba(0,0,0,0.5)',
              textAlign: 'center',
              '@media (max-width: 768px)': {
                fontSize: '3rem',
              },
              '@media (max-width: 480px)': {
                fontSize: '2.5rem',
              }
            }}
          >
            Why Join 10D as a Customer?
          </Typography>
          <Typography
            variant="h4"
            component="p"
            gutterBottom
            sx={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              fontSize: '1.75rem',
              color: 'white',
              maxWidth: '700px',
              mb: 8,
              mx: 'auto',
              textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              lineHeight: 1.4,
              textAlign: 'center',
              minHeight: '3rem'
            }}
          >
            <Typewriter
              onInit={(typewriter) => {
                typewriter
                  .pauseFor(2000)
                  .typeString('Get exclusive discounts at verified local businesses.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Support your community while saving money.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Discover hidden gems in your neighborhood.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Simple QR code verification for instant savings.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(500)
                  .typeString('Join thousands of smart shoppers already saving.')
                  .pauseFor(3000)
                  .deleteAll()
                  .pauseFor(1000)
                  .start();
              }}
              options={{
                loop: true,
                delay: 50,
                cursor: '|',
                cursorClassName: 'typewriter-cursor',
              }}
            />
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                backgroundColor: 'white',
                color: 'black',
                fontSize: '1.125rem',
                fontWeight: 700,
                px: '2rem',
                py: '1rem',
                borderRadius: '9999px',
                border: 'none',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'white',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                },
              }}
            >
              Start Saving Today
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                backgroundColor: 'transparent',
                borderColor: 'white',
                borderWidth: '2px',
                color: 'white',
                fontSize: '1.125rem',
                fontWeight: 700,
                px: '2rem',
                py: '1rem',
                borderRadius: '9999px',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'white',
                  color: 'black',
                  transform: 'scale(1.05)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                },
              }}
            >
              Learn More
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Why Users Should Subscribe to 10D Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 3 }}>
              Why Users Should Subscribe to 10D
            </Typography>
            <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
              Become a 10D member and unlock exclusive access to local business discounts.
              It's not just about discovering amazing local experiences.
            </Typography>
          </Box>


        </Container>
      </Box>

      {/* Save Money on Every Purchase Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Save Money on Every Purchase
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Get exclusive discounts at local businesses just by being a 10D member.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Savings sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Average Savings
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Save an average of 15-25% on every purchase at partner businesses
                </Typography>
                <Chip label="15-25% OFF" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <LocalOffer sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Exclusive Deals
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Access to member-only discounts that regular customers never see
                </Typography>
                <Chip label="Members Only" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <TrendingUp sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Cumulative Savings
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Watch your savings grow with every visit to partner businesses
                </Typography>
                <Chip label="Growing Savings" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <AccountBalance sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Smart Spending
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Make your money go further with strategic discount usage
                </Typography>
                <Chip label="Smart Money" color="success" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Discover Local Businesses Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Discover Local Businesses
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Find hidden gems in your neighborhood that you never knew existed.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Store sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Hidden Gems
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Discover amazing local businesses that aren't on popular platforms
                </Typography>
                <Chip label="1000+ Partners" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <LocationOn sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Local Focus
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Find businesses in your neighborhood and surrounding areas
                </Typography>
                <Chip label="Nearby Deals" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Category sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Diverse Categories
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Explore businesses across all categories and industries
                </Typography>
                <Chip label="All Categories" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Verified sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Verified Partners
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  All businesses are verified and quality-checked by our team
                </Typography>
                <Chip label="Quality Assured" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Simple QR Code Verification Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Simple QR Code Verification
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Just scan your unique QR code at any partner business for instant verification.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <QrCode sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  One Scan
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Single QR code scan provides instant verification and discount
                </Typography>
                <Chip label="Instant Access" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Speed sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Lightning Fast
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Get your discount in seconds, no waiting or paperwork needed
                </Typography>
                <Chip label="Seconds Only" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Security sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Secure Process
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Your QR code is unique and secure, preventing fraud and misuse
                </Typography>
                <Chip label="100% Secure" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Smartphone sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Mobile Friendly
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Use your smartphone to scan and access discounts anywhere
                </Typography>
                <Chip label="Mobile Ready" color="warning" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Exclusive Member Deals Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Exclusive Member Deals
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Access to deals that regular customers never see.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Star sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  VIP Access
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Get first access to limited-time offers and flash sales
                </Typography>
                <Chip label="VIP Priority" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <NewReleases sx={{ fontSize: 60, color: '#e6b8a2', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  New Deals Daily
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Fresh offers and discounts added every single day
                </Typography>
                <Chip label="Daily Updates" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Celebration sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Special Events
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Exclusive access to member-only events and promotions
                </Typography>
                <Chip label="Event Access" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Loyalty sx={{ fontSize: 60, color: '#5a9b9a', mb: 3 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Loyalty Rewards
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1rem', lineHeight: 1.6 }}>
                  Earn additional rewards and bonuses for frequent usage
                </Typography>
                <Chip label="Bonus Rewards" color="error" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Verified Business Partners Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Verified Business Partners
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Discover trusted local businesses that have partnered with 10D to offer exclusive discounts to our members.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Store sx={{ fontSize: 60, color: '#5a9b9a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                  Local Retail Stores
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 2 }}>
                  Fashion, electronics, home goods, and specialty stores
                </Typography>
                <Chip label="10-25% OFF" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Restaurant sx={{ fontSize: 60, color: '#5a9b9a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                  Restaurants & Cafes
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 2 }}>
                  Fine dining, casual eateries, and coffee shops
                </Typography>
                <Chip label="15-30% OFF" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <LocalOffer sx={{ fontSize: 60, color: '#5a9b9a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                  Service Providers
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 2 }}>
                  Salons, spas, fitness centers, and repair services
                </Typography>
                <Chip label="20-35% OFF" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 3, '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <LocalActivity sx={{ fontSize: 60, color: '#5a9b9a', mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                  Entertainment
                </Typography>
                <Typography variant="body2" sx={{ color: '#2d2d2d', mb: 2 }}>
                  Movie theaters, bowling alleys, and activity centers
                </Typography>
                <Chip label="25-40% OFF" color="primary" variant="outlined" sx={{ fontWeight: 'bold' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            How 10D Customer Experience Works
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            Our simple 4-step process makes saving money and discovering local businesses effortless and enjoyable.
          </Typography>
          <Grid container spacing={4}>
            {howItWorks.map((step, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    textAlign: 'center',
                    p: 4,
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' },
                  }}
                  elevation={2}
                >
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      backgroundColor: '#F1F5F9',
                      mb: 2
                    }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#5a9b9a' }}>
                        {step.step}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 3 }}>{step.icon}</Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#2d2d2d', fontSize: '1rem', lineHeight: 1.6 }}>
                    {step.description}
                  </Typography>
                  <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2, border: '1px solid #eeeeee' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 500 }}>
                      💡 {step.tip || 'Quick and easy process'}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 8, p: 4, backgroundColor: 'white', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '2px solid #E2E8F0' }}>
            <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 3, textAlign: 'center' }}>
              Why This Process Works So Well
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircle sx={{ color: '#e3662d', mr: 2, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                    Instant Verification
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#2d2d2d', pl: 4 }}>
                  QR code scanning provides immediate verification, so you get your discount right away without any waiting.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Security sx={{ color: '#e3662d', mr: 2, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                    Secure & Reliable
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#2d2d2d', pl: 4 }}>
                  All partner businesses are verified and monitored to ensure you always get the promised discounts.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Support sx={{ color: '#e3662d', mr: 2, fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                    24/7 Support
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#2d2d2d', pl: 4 }}>
                  Our customer support team is always available to help if you encounter any issues with discounts.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>

      {/* Exclusive Member Discounts Section */}
      <Box sx={{ backgroundColor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Exclusive Member Discounts
          </Typography>
          <Typography variant="h6" component="h3" sx={{ color: '#2d2d2d', maxWidth: 800, mx: 'auto', fontSize: '1.25rem', lineHeight: 1.6, textAlign: 'center', mb: 6 }}>
            As a 10D member, you get access to exclusive discounts that are not available to regular customers.
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Discount sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Daily Deals
                </Typography>
                <Typography variant="body1" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                  New exclusive offers every day from our partner businesses
                </Typography>
                <Chip label="Up to 50% OFF" color="success" variant="outlined" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Star sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Premium Offers
                </Typography>
                <Typography variant="body1" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                  Special discounts on premium products and services
                </Typography>
                <Chip label="Premium Access" color="success" variant="outlined" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ height: '100%', textAlign: 'center', p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' } }}>
                <Celebration sx={{ fontSize: 60, color: '#e3662d', mb: 3 }} />
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d', mb: 2 }}>
                  Seasonal Promotions
                </Typography>
                <Typography variant="body1" sx={{ color: '#2d2d2d', mb: 3, fontSize: '1.1rem', lineHeight: 1.6 }}>
                  Limited-time offers during holidays and special occasions
                </Typography>
                <Chip label="Limited Time" color="success" variant="outlined" sx={{ fontWeight: 'bold', fontSize: '1rem' }} />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Subscription Plans Section */}
      <Box sx={{ backgroundColor: '#f5f5f5', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" textAlign="center" gutterBottom sx={{ mb: 6, fontWeight: 'bold', color: '#2d2d2d' }}>
            Choose Your 10D Customer Plan
          </Typography>
          <Grid container spacing={4}>
            {subscriptionPlans.map((plan, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    position: 'relative',
                    border: plan.popular ? 2 : 1,
                    borderColor: plan.popular ? '#2563EB' : '#E2E8F0',
                    '&:hover': { transform: 'translateY(-8px)', transition: 'transform 0.3s ease' },
                  }}
                  elevation={plan.popular ? 8 : 2}
                >
                  {plan.popular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold',
                        backgroundColor: '#2563EB',
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h4" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2d2d2d' }}>
                      {plan.name}
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: '#5a9b9a' }}>
                        {plan.price}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#2d2d2d', fontSize: '1rem' }}>
                        {plan.period}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, textAlign: 'left' }}>
                      {plan.features.map((feature, index) => (
                        <Box component="li" key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                          <CheckCircle sx={{ color: '#5a9b9a', mr: 1, fontSize: 20 }} />
                          <Typography variant="body2" sx={{ color: '#2d2d2d', fontSize: '1rem' }}>{feature}</Typography>
                        </Box>
                      ))}
                    </Box>
                    <Button
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      sx={{
                        mt: 3,
                        backgroundColor: plan.popular ? '#2563EB' : 'transparent',
                        color: plan.popular ? 'white' : '#2563EB',
                        borderColor: '#2563EB',
                        '&:hover': {
                          backgroundColor: plan.popular ? '#1D4ED8' : '#2563EB',
                          color: 'white',
                        }
                      }}
                      component={Link}
                      to="/register"
                    >
                      Choose Plan
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #f17a4a 0%, #f8a17a 100%)',
        py: 12,
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(241, 122, 74, 0.3)',
      }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 6, fontSize: '3rem', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Ready to Start Your 10D Journey?
            </Typography>
            <Typography variant="h5" sx={{ mb: 8, fontSize: '1.5rem', lineHeight: 1.6, maxWidth: 800, mx: 'auto', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>
              Join thousands of 10D members already enjoying exclusive discounts at local businesses.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: '#5a9b9a',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  px: 10,
                  py: 3,
                  borderRadius: '16px',
                  boxShadow: '0 8px 32px rgba(90, 155, 154, 0.4)',
                  '&:hover': {
                    backgroundColor: '#4a8a89',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 16px 48px rgba(90, 155, 154, 0.6)'
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Join 10D Today
              </Button>
              <Button
                component={Link}
                to="/business"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  borderWidth: 2,
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  px: 10,
                  py: 3,
                  borderRadius: '16px',
                  '&:hover': {
                    borderColor: '#5a9b9a',
                    backgroundColor: '#5a9b9a',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 32px rgba(90, 155, 154, 0.4)'
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Partner with 10D
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <BackToTop />
    </Box>
  );
};

export default CustomersPage;
